import { FormattedMessage, useIntl } from 'react-intl';
import { createContext, useContext, useEffect, useState } from 'react';
import { Button, Divider, Form, Modal, Skeleton } from 'antd';

import { PageProps } from '@/types/PageProps';

import { SaveButton } from '@/components/_common/Buttons';
import {
  AccountPayableReleaseFormValues,
  AccountPayableReleaseUpdateRequest,
  AccountPayableReturnListInstallments,
  InstallmentCancelRequest,
  InstallmentSituation,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import {
  useAccountPayableAndInstallmentUpdate,
  useAccountPayableReturnListInstallments,
  useInstallmentCancel,
} from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import { Helmet } from 'react-helmet';
import { useWindowSize } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useEmployeeDetailReduced } from '@/core/domain/enterprise/Employees/Employees.hooks';
import { useSupplier } from '@/core/domain/people/Suppliers/Suppliers.hooks';
import ReleaseDetailForm from '@/components/Registry/BillToPay/ReleaseForm/ReleaseDetailForm';
import { DateTimeFormat } from '@/utils/moment';
import AccontPayableButtonsRepeated from '@/components/Registry/BillToPay/ReleaseForm/ReleaseDetailForm/AccontPayableButtonsRepeated';
import handleFormChange from '@/hooks/handleFormChange';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { DefaultRouteTabs } from '@/components/_common';
import { useBillToPayReleaseDetailContext } from '@/components/Financial/BillToPay/ReleaseDetailProvider';
import { UseQueryResult } from 'react-query';

interface AccountPayableContextParams {
  accountPayableHook: UseQueryResult<
    AccountPayableReturnListInstallments,
    unknown
  >;
  accountPayable?: AccountPayableReturnListInstallments;
  nextInstallment: VoidFunction;
  prevInstallment: VoidFunction;
  currentInstallment: number;
  totalInstallments: number;
}

const AccountPayableContext = createContext({} as AccountPayableContextParams);

export const useAccountPayableContext = () => useContext(AccountPayableContext);

function ReleasesDetailsPage({ route }: PageProps) {
  const intl = useIntl();
  const { isMobile } = useWindowSize();
  const history = useHistory();
  const [currentInstallment, setCurrentInstallment] = useState<number>(0);
  const [formChange, setFormChange] = useState({});
  const disabled = !Object.keys(formChange).some((key) => !!key);

  const [form] = Form.useForm<AccountPayableReleaseFormValues>();

  const { installmentPayableId, accountPayableId } =
    useBillToPayReleaseDetailContext();

  const accountPayable = useAccountPayableReturnListInstallments(
    Number(accountPayableId),
  );

  useEffect(() => {
    if (installmentPayableId && accountPayable.data) {
      setCurrentInstallment(
        accountPayable.data?.installmentPayables.findIndex(
          (item) => installmentPayableId === item.id,
        ),
      );
    }
  }, [installmentPayableId, accountPayable.data]);

  const installmentData =
    accountPayable.data?.installmentPayables[currentInstallment];

  const totalInstallments =
    accountPayable.data?.installmentPayables.length || 0;

  function goToPage(id?: number) {
    const paths = window.location.pathname
      .split('/')
      .filter((path) => !!path)
      .slice(0, -2);

    history.push(`/${paths.join('/')}/${id}/general`);
  }

  function nextInstallment() {
    setCurrentInstallment(currentInstallment + 1);
    goToPage(
      accountPayable.data?.installmentPayables[currentInstallment + 1].id,
    );
  }

  function prevInstallment() {
    setCurrentInstallment(currentInstallment - 1);
    goToPage(
      accountPayable.data?.installmentPayables[currentInstallment - 1].id,
    );
  }

  const employee = useEmployeeDetailReduced(accountPayable.data?.employeeId);
  const supplier = useSupplier(accountPayable.data?.supplierId);

  const updateMutation = useAccountPayableAndInstallmentUpdate();
  const cancelMutation = useInstallmentCancel();

  useEffect(() => {
    if (accountPayable.data) {
      const { installmentPayables, ...restAccountPayable } =
        accountPayable.data;

      const installmentPayable = installmentPayables[currentInstallment];

      form.setFieldsValue({
        ...restAccountPayable,
        ...installmentPayable,
        dueDate: installmentPayable?.dueDate
          ? moment(installmentPayable?.dueDate)
          : undefined,
        emissionDate: installmentPayable?.emissionDate
          ? moment(installmentPayable?.emissionDate)
          : undefined,
        payDate: installmentPayable?.payDate
          ? moment(installmentPayable?.payDate)
          : undefined,
        installmentValue: installmentPayable?.installmentValue || 0,
        discountIncrease: installmentPayable?.discountIncrease || 0,
        credit: installmentPayable?.credit || 0,
        totalAmount:
          (installmentPayable?.installmentValue || 0) +
          (installmentPayable?.discountIncrease || 0) -
          (installmentPayable?.credit || 0),
      });
    }
  }, [accountPayable.data, currentInstallment]);

  useEffect(() => {
    if (employee.data) {
      form.setFieldsValue({ employee: employee.data.name });
    } else if (supplier.data) {
      form.setFieldsValue({ supplier: supplier.data.name });
    }
  }, [employee.dataUpdatedAt, supplier.dataUpdatedAt]);

  const handleUpdateAccountPayableAndInstallment = async () => {
    try {
      const values = await form.validateFields();

      const obj: AccountPayableReleaseUpdateRequest = {
        ...values,
        accountPayableId: Number(accountPayableId),
        installmentPayableId: Number(installmentPayableId),
        emissionDate: values.emissionDate.format(DateTimeFormat),
        dueDate: values.dueDate.format(DateTimeFormat),
        currentSituation: installmentData?.situation,
      };

      const response = await updateMutation.mutateAsync(obj);
      setFormChange({});
      return response;
    } catch {}
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: 'Confirmação',
      content: accountPayable.data?.isOriginalCommission
        ? 'Esta conta a pagar está vinculada à comissão. Ao excluir a última parcela, o valor será estornado automaticamente. Deseja continuar com a exclusão?'
        : 'O processo de exclusão de parcelas é irreversível. Deseja realmente excluir a parcela?',
      onOk: async () => {
        const obj: InstallmentCancelRequest = {
          id: Number(accountPayableId),
          installmentPayableId: Number(installmentPayableId),
        };
        await cancelMutation.mutateAsync(obj);

        history.push('/app/financial/billToPay/releaseBillToPay');
      },
      okType: 'danger',
    });
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <AccountPayableContext.Provider
        value={{
          accountPayableHook: accountPayable,
          accountPayable: accountPayable.data,
          nextInstallment,
          prevInstallment,
          currentInstallment,
          totalInstallments,
        }}
      >
        <DefaultRouteTabs
          permissions={route.permissions}
          route={{ ...route.parent, key: route.key }}
          baseKey={installmentPayableId.toString()}
          basePath={window.location.pathname.replace('/general', '')}
          extra={
            <SpaceForButton flexDirection={isMobile ? 'column-reverse' : 'row'}>
              <Button
                block
                danger
                onClick={handleRemoveClick}
                disabled={
                  accountPayable.data?.installmentPayables[currentInstallment]
                    ?.situation !== InstallmentSituation.open ||
                  !hasPermissions([
                    Authorities.FINANCIAL_ACCOUNT_PAYABLE_EDIT,
                    Authorities.FINANCIAL_ACCOUNT_PAYABLE_INSTALLMENTS_DELETE,
                  ])
                }
              >
                <FormattedMessage id="commons.entity.remove" />
              </Button>

              <SaveButton
                block
                type="primary"
                onClick={handleUpdateAccountPayableAndInstallment}
                loading={updateMutation.isLoading}
                disabled={
                  !hasPermissions([
                    Authorities.FINANCIAL_ACCOUNT_PAYABLE_EDIT,
                  ]) ||
                  accountPayable.data?.installmentPayables[currentInstallment]
                    ?.situation === InstallmentSituation.settled ||
                  accountPayable.data?.installmentPayables[currentInstallment]
                    ?.situation === InstallmentSituation.canceled
                }
              >
                <FormattedMessage id="commons.entity.save" />
              </SaveButton>
            </SpaceForButton>
          }
        >
          <Skeleton loading={accountPayable.isLoading}>
            <Form.Provider
              onFormChange={(name, info) =>
                handleFormChange(name, info, setFormChange)
              }
            >
              <ReleaseDetailForm
                installment={
                  accountPayable.data?.installmentPayables[currentInstallment]
                }
                form={form}
                isEdit
              />
            </Form.Provider>

            <Divider />

            <AccontPayableButtonsRepeated
              disabledButtonBack={disabled}
              handleSave={handleUpdateAccountPayableAndInstallment}
            />
          </Skeleton>
        </DefaultRouteTabs>
      </AccountPayableContext.Provider>
    </>
  );
}

export default ReleasesDetailsPage;
